// Por qué un bloque de la ficha no trae dato. Los cuatro motivos piden cosas distintas: solo la
// falla de lectura se arregla reintentando.
import { ApiError, SessionExpiredError } from '@/api/errors';

export type CreditFileFailure = 'forbidden' | 'expired' | 'not-found' | 'unavailable';

/** El motivo que declara cada `code`. El status solo agrupa por familia y varios silos lo mienten. */
const FAILURE_BY_CODE: Record<string, CreditFileFailure> = {
    PERMISSION_DENIED: 'forbidden',
    SESSION_EXPIRED: 'expired',
    UNAUTHORIZED: 'expired',
    NOT_FOUND: 'not-found',
};

/**
 * `null` cuando no hubo error. El `code` manda y el `httpStatus` es la red de seguridad: varios
 * silos mandan el status de otra familia y creerle al número ofrece reintentos que no funcionan.
 */
export function classifyFailure(error: unknown): CreditFileFailure | null {
    if (!error) return null;
    // La sesión muerta la atiende el manejo central (App.tsx → /login); mientras navega, el bloque
    // dice la verdad en vez de ofrecer un reintento que no puede funcionar.
    if (error instanceof SessionExpiredError) return 'expired';
    if (error instanceof ApiError) {
        // El 403 va antes de la tabla: es el único status que nunca miente, y leerlo como sesión
        // muerta dejaría a un rol sin permiso cayendo al mock editable de Políticas.
        if (error.httpStatus === 403) return 'forbidden';
        if (Object.hasOwn(FAILURE_BY_CODE, error.code)) return FAILURE_BY_CODE[error.code];
        if (error.httpStatus === 401) return 'expired';
        if (error.httpStatus === 404) return 'not-found';
    }
    return 'unavailable';
}

/** Solo la falla de lectura se arregla reintentando; permiso, sesión y 404 no. */
export function isRetryableFailure(failure: CreditFileFailure): boolean {
    return failure === 'unavailable';
}

export interface FailureDescription {
    title: string;
    body: string;
}

/** El verbo concuerda con el sujeto: «los pagos» y «las cuotas» van en plural. */
function couldVerb(subject: string): string {
    return /^(los|las) /.test(subject) ? 'pudieron' : 'pudo';
}

/** `subject` nombra el bloque que falló («la amortización») para que el aviso no sea genérico. */
export function describeFailure(failure: CreditFileFailure, subject: string): FailureDescription {
    switch (failure) {
        case 'forbidden':
            return {
                title: `No tienes permiso para ver ${subject}`,
                body: 'Tu rol no tiene los permisos de crédito que este bloque necesita. Reintentar no lo cambia: contacta a quien administra los roles.',
            };
        case 'expired':
            return {
                title: `Tu sesión expiró`,
                body: 'Inicia sesión de nuevo para ver esta información.',
            };
        case 'not-found':
            return {
                title: `No encontramos ${subject}`,
                body: 'El crédito no existe o ya no está disponible en el silo.',
            };
        case 'unavailable':
            return {
                title: `No se ${couldVerb(subject)} cargar ${subject}`,
                body: 'La lectura falló. Vuelve a intentar; si sigue pasando, repórtalo.',
            };
    }
}

/** Versión de una línea, para las sub-líneas del hero donde no cabe el aviso completo. */
export function briefFailureNote(failure: CreditFileFailure, subject: string): string {
    switch (failure) {
        case 'forbidden':
            return `No tienes permiso para ver ${subject}.`;
        case 'expired':
            return 'Tu sesión expiró.';
        case 'not-found':
            return `No encontramos ${subject}.`;
        case 'unavailable':
            return `No se ${couldVerb(subject)} leer ${subject}.`;
    }
}
